/**
 * Loyalty points-only products in third-party search results (Rapid Search).
 *
 * The search app renders its own result markup client-side, so the theme's
 * points-only price logic (snippets/price.liquid) never applies there and reward
 * products show their money price. This script replaces that price with the
 * points cost ("500 points") and hides the app's add-to-cart button for them.
 *
 * It avoids the search app's generated (hashed) class names: products are found
 * by the stable `.rps_list_item[data-handle]` rows of the instant-search dropdown
 * (or by product links on the results page), matched against the loyalty
 * reward-products list (/apps/loyalty/reward-products, cached for the session),
 * and the text nodes holding a money amount are rewritten.
 */
(function () {
  var PROXY_URL = "/apps/loyalty/reward-products";
  var CACHE_KEY = "fpo:search-reward-products";
  var CACHE_TTL = 5 * 60 * 1000;
  // Instant-search dropdown + the app's full results page.
  var CONTAINERS = "#rapid-search-results, .rs-resultpage, [class*='rps-'][class*='results-page']";
  // Instant-search product rows carry the product handle (stable `rps_` class).
  var ITEMS = ".rps_list_item[data-handle]";
  var MONEY =/(\d[\d\s.,]*\s?(€|EUR)|(€|EUR)\s?\d)/;
  var DONE = "data-fpo-search-done";

  var costByHandle = null; // { handle: pointsCost }
  var loading = null;

  function handleFromUrl(url) {
    var m = String(url || "").match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]).toLowerCase() : "";
  }

  function readCache() {
    try {
      var c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
      if (c && c.map && Date.now() - c.at < CACHE_TTL) return c.map;
    } catch (e) {}
    return null;
  }

  function load() {
    if (costByHandle) return Promise.resolve(costByHandle);
    if (loading) return loading;
    var req = window.LoyaltyCache
      ? window.LoyaltyCache.get(PROXY_URL)
      : fetch(PROXY_URL, { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); });
    loading = Promise.resolve(req)
      .then(function (data) {
        var map = {};
        ((data && data.rewardProducts) || []).forEach(function (rp) {
          var handle = handleFromUrl(rp.url);
          if (handle && rp.pointsCost > 0) map[handle] = rp.pointsCost;
        });
        costByHandle = map;
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), map: map })); } catch (e) {}
        return map;
      })
      .catch(function () { loading = null; return costByHandle || {}; });
    return loading;
  }

  // Visible text nodes inside `root` that hold a money amount.
  function moneyTextNodes(root) {
    var out = [];
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      var parent = node.parentElement;
      if (!parent || parent.style.display === "none") continue;
      if (MONEY.test(node.nodeValue || "")) out.push(node);
    }
    return out;
  }

  // Rewrite one result item: first amount → points, further ones (compare-at /
  // unit price) hidden, and the search app's add-to-cart hidden (it would add the
  // product for money, which checkout then refuses).
  function rewrite(root, cost) {
    moneyTextNodes(root).forEach(function (node, index) {
      if (index === 0) node.nodeValue = cost + " points";
      else node.parentElement.style.display = "none";
    });
    if (root.getAttribute(DONE) !== "1") {
      root.setAttribute(DONE, "1");
      root.querySelectorAll("button, [class*='add-to-cart'], [class*='addToCart']").forEach(function (btn) {
        btn.style.display = "none";
      });
    }
  }

  // Smallest ancestor of the link that holds this product's price and no other product.
  function itemRoot(link, handle, container) {
    var el = link;
    for (var i = 0; i < 6 && el && el !== container; i++) {
      var others = Array.prototype.some.call(el.querySelectorAll("a[href*='/products/']"), function (a) {
        return handleFromUrl(a.getAttribute("href")) !== handle;
      });
      if (others) return null;
      if (moneyTextNodes(el).length) return el;
      el = el.parentElement;
    }
    return null;
  }

  function apply() {
    if (!costByHandle) return;

    // Instant-search dropdown: each product is a `.rps_list_item[data-handle]`
    // (no link), appended at the end of <body>.
    document.querySelectorAll(ITEMS).forEach(function (item) {
      var cost = costByHandle[String(item.getAttribute("data-handle") || "").toLowerCase()];
      if (cost) rewrite(item, cost);
    });

    // Results page / any layout that renders products as links.
    document.querySelectorAll(CONTAINERS).forEach(function (container) {
      container.querySelectorAll("a[href*='/products/']").forEach(function (link) {
        var handle = handleFromUrl(link.getAttribute("href"));
        var cost = costByHandle[handle];
        if (!cost) return;
        var root = itemRoot(link, handle, container);
        if (root) rewrite(root, cost);
      });
    });
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      if (!document.querySelector(ITEMS + ", " + CONTAINERS)) return;
      if (costByHandle) apply();
      else load().then(apply);
    });
  }

  costByHandle = readCache();
  // Warm the list before the first search so the money price never flashes.
  var warm = function () { load().then(apply); };
  if ("requestIdleCallback" in window) window.requestIdleCallback(warm, { timeout: 3000 });
  else window.setTimeout(warm, 1500);

  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  schedule();
})();
